<template>
  <div class="upload-with-webp-page">
    <el-card class="box-card">
      <div slot="header" class="clearfix">
        <span>文件上传 - WebP 配置</span>
      </div>

      <!-- WebP 配置区域 -->
      <el-form :model="uploadConfig" label-width="140px" class="config-form">
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="WebP 转换">
              <el-switch
                v-model="uploadConfig.convertToWebP"
                active-text="启用"
                inactive-text="禁用"
                active-color="#13ce66"
                @change="handleConvertChange"
              >
              </el-switch>
              <div class="form-tip">
                {{ uploadConfig.convertToWebP ? '图片将自动转换为 WebP 格式' : '保持原始图片格式' }}
              </div>
            </el-form-item>
          </el-col>

          <el-col :span="12">
            <el-form-item label="WebP 质量">
              <el-select
                v-model="uploadConfig.webpQuality"
                placeholder="请选择质量"
                :disabled="!uploadConfig.convertToWebP"
                style="width: 100%"
              >
                <el-option
                  v-for="item in qualityOptions"
                  :key="item.value"
                  :label="item.label"
                  :value="item.value"
                >
                  <div class="quality-option">
                    <div class="option-label">{{ item.label }}</div>
                    <div class="option-detail">
                      <el-tag size="mini" type="info">{{ item.description }}</el-tag>
                      <el-tag size="mini" type="warning">文件: {{ item.fileSize }}</el-tag>
                      <el-tag size="mini" type="success">画质: {{ item.quality }}</el-tag>
                    </div>
                  </div>
                </el-option>
              </el-select>
              <div class="form-tip">
                当前选择: {{ currentQualityInfo }}
              </div>
            </el-form-item>
          </el-col>
        </el-row>

        <!-- 配置预览 -->
        <el-form-item label="当前配置">
          <el-tag type="primary" size="medium">存储桶: kanbei</el-tag>
          <el-tag :type="uploadConfig.convertToWebP ? 'success' : 'info'" size="medium">
            {{ uploadConfig.convertToWebP ? `WebP 转换: 启用 (${uploadConfig.webpQuality})` : 'WebP 转换: 禁用' }}
          </el-tag>
          <el-tag type="warning" size="medium">上传进度: 显示</el-tag>
        </el-form-item>
      </el-form>

      <el-divider></el-divider>

      <!-- 上传区域 -->
      <el-tabs v-model="activeTab">
        <!-- 图片上传 -->
        <el-tab-pane label="图片上传" name="image">
          <div class="upload-section">
            <h3>上传图片</h3>
            <p class="section-tip">支持 JPEG, PNG, GIF, BMP, TIFF 等格式，单个文件最大 50MB</p>

            <ImageUpload
              v-model="imageUrls"
              :convert-to-web-p="uploadConfig.convertToWebP"
              :webp-quality="uploadConfig.webpQuality"
              bucket="kanbei"
              :limit="10"
              :fileSize="50"
              :show-progress="true"
            />

            <!-- 上传结果 -->
            <div v-if="imageUrls" class="upload-result">
              <el-alert title="上传成功" type="success" :closable="false">
                <div>已上传的图片URL:</div>
                <pre>{{ imageUrls }}</pre>
              </el-alert>
            </div>
          </div>
        </el-tab-pane>

        <!-- 文件上传 -->
        <el-tab-pane label="文件上传" name="file">
          <div class="upload-section">
            <h3>上传文件</h3>
            <p class="section-tip">支持文档、视频等各类文件，单个文件最大 50MB</p>

            <FileUpload
              v-model="fileIds"
              :convert-to-web-p="uploadConfig.convertToWebP"
              :webp-quality="uploadConfig.webpQuality"
              bucket="kanbei"
              :limit="5"
              :fileSize="50"
              :file-type="['pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'txt', 'zip', 'rar']"
              :show-progress="true"
            />

            <!-- 上传结果 -->
            <div v-if="fileIds" class="upload-result">
              <el-alert title="上传成功" type="success" :closable="false">
                <div>已上传的文件ID:</div>
                <pre>{{ fileIds }}</pre>
              </el-alert>
            </div>
          </div>
        </el-tab-pane>

        <!-- 图片预览上传 -->
        <el-tab-pane label="图片预览上传" name="preview">
          <div class="upload-section">
            <h3>图片预览上传（支持外部链接）</h3>
            <p class="section-tip">支持本地上传和外部图片链接输入</p>

            <ImagePreviewWithUpload
              v-model="previewImages"
              :convert-to-web-p="uploadConfig.convertToWebP"
              :webp-quality="uploadConfig.webpQuality"
              bucket="kanbei"
              :limit="5"
              :show-external-img-link="true"
              :show-progress="true"
            />

            <!-- 上传结果 -->
            <div v-if="previewImages && previewImages.length" class="upload-result">
              <el-alert title="上传成功" type="success" :closable="false">
                <div>已上传的图片:</div>
                <pre>{{ JSON.stringify(previewImages, null, 2) }}</pre>
              </el-alert>
            </div>
          </div>
        </el-tab-pane>
      </el-tabs>

      <!-- 使用说明 -->
      <el-divider></el-divider>
      <el-collapse v-model="activeCollapse">
        <el-collapse-item title="📖 WebP 质量说明" name="1">
          <el-table :data="qualityOptions" border style="width: 100%">
            <el-table-column prop="label" label="质量级别" width="200"></el-table-column>
            <el-table-column prop="description" label="适用场景" width="200"></el-table-column>
            <el-table-column prop="fileSize" label="文件大小" width="100"></el-table-column>
            <el-table-column prop="quality" label="画质" width="100"></el-table-column>
            <el-table-column label="操作">
              <template slot-scope="scope">
                <el-button size="mini" @click="applyQuality(scope.row.value)">
                  应用此配置
                </el-button>
              </template>
            </el-table-column>
          </el-table>
        </el-collapse-item>

        <el-collapse-item title="💡 使用提示" name="2">
          <ul class="tip-list">
            <li><strong>存储桶固定为 kanbei:</strong> 所有文件将自动上传到 kanbei 存储桶</li>
            <li><strong>WebP 转换:</strong> 启用后，支持的图片格式（JPEG/PNG/GIF/TIFF/BMP）将自动转换为 WebP</li>
            <li><strong>质量选择:</strong>
              <ul>
                <li>High - 适合需要高质量的专业图片</li>
                <li>Medium - 推荐，平衡质量和文件大小</li>
                <li>Low - 适合缩略图，优先考虑加载速度</li>
                <li>Smallest - 极致压缩，适合大量小图</li>
                <li>Extreme - 极限压缩，文件最小但质量较差</li>
              </ul>
            </li>
            <li><strong>上传进度:</strong> 默认显示上传进度条，实时查看上传状态</li>
            <li><strong>文件限制:</strong> 单文件最大 50MB，批量上传建议总大小 ≤ 100MB</li>
          </ul>
        </el-collapse-item>

        <el-collapse-item title="🎯 快速配置" name="3">
          <div class="quick-config">
            <el-button type="primary" @click="setPreset('high')">高质量配置</el-button>
            <el-button type="success" @click="setPreset('balanced')">平衡配置（推荐）</el-button>
            <el-button type="warning" @click="setPreset('small')">小体积配置</el-button>
            <el-button type="info" @click="setPreset('original')">原格式（不转换）</el-button>
          </div>
        </el-collapse-item>
      </el-collapse>
    </el-card>
  </div>
</template>

<script>
import ImageUpload from '@/components/ImageUpload'
import FileUpload from '@/components/FileUpload'
import ImagePreviewWithUpload from '@/components/ImagePreviewWithUpload'
import { convertOptions, qualityOptions, defaultWebpConfig } from '@/config/webp-options'

export default {
  name: 'UploadWithWebp',
  components: {
    ImageUpload,
    FileUpload,
    ImagePreviewWithUpload
  },
  data() {
    return {
      // WebP 配置选项
      convertOptions,
      qualityOptions,

      // 上传配置
      uploadConfig: {
        convertToWebP: defaultWebpConfig.convertToWebP,
        webpQuality: defaultWebpConfig.webpQuality
      },

      // 上传结果
      imageUrls: '',
      fileIds: '',
      previewImages: [],

      // UI 状态
      activeTab: 'image',
      activeCollapse: []
    }
  },
  computed: {
    // 当前质量信息
    currentQualityInfo() {
      if (!this.uploadConfig.convertToWebP) {
        return '未启用 WebP 转换'
      }
      const quality = this.qualityOptions.find(q => q.value === this.uploadConfig.webpQuality)
      return quality ? `${quality.description} - 文件${quality.fileSize}，画质${quality.quality}` : ''
    }
  },
  methods: {
    // WebP 转换开关变化
    handleConvertChange(value) {
      if (value) {
        this.$message.success('已启用 WebP 转换')
      } else {
        this.$message.info('已禁用 WebP 转换，将保持原格式')
      }
    },

    // 应用质量配置
    applyQuality(quality) {
      this.uploadConfig.convertToWebP = true
      this.uploadConfig.webpQuality = quality
      const qualityInfo = this.qualityOptions.find(q => q.value === quality)
      this.$message.success(`已应用 ${qualityInfo.label} 配置`)
    },

    // 快速配置预设
    setPreset(preset) {
      const presets = {
        high: {
          convertToWebP: true,
          webpQuality: 'high'
        },
        balanced: {
          convertToWebP: true,
          webpQuality: 'medium'
        },
        small: {
          convertToWebP: true,
          webpQuality: 'smallest'
        },
        original: {
          convertToWebP: false,
          webpQuality: 'medium'
        }
      }

      this.uploadConfig = { ...presets[preset] }

      const messages = {
        high: '高质量配置：适合专业图片',
        balanced: '平衡配置：推荐使用',
        small: '小体积配置：适合大量图片',
        original: '原格式：不进行转换'
      }

      this.$message.success(messages[preset])
    }
  }
}
</script>

<style scoped lang="scss">
.upload-with-webp-page {
  padding: 20px;

  .box-card {
    margin-bottom: 20px;
  }

  .config-form {
    .form-tip {
      font-size: 12px;
      color: #909399;
      margin-top: 5px;
    }

    .el-tag {
      margin-right: 10px;
    }
  }

  .upload-section {
    padding: 20px;
    background: #f5f7fa;
    border-radius: 4px;
    margin-bottom: 20px;

    h3 {
      margin-top: 0;
      margin-bottom: 10px;
      color: #303133;
    }

    .section-tip {
      color: #909399;
      font-size: 14px;
      margin-bottom: 20px;
    }
  }

  .upload-result {
    margin-top: 20px;

    pre {
      background: #f4f4f5;
      padding: 10px;
      border-radius: 4px;
      overflow-x: auto;
      font-size: 12px;
    }
  }

  .quality-option {
    .option-label {
      font-weight: bold;
      margin-bottom: 5px;
    }

    .option-detail {
      .el-tag {
        margin-right: 5px;
        margin-top: 3px;
      }
    }
  }

  .tip-list {
    line-height: 1.8;
    color: #606266;

    ul {
      margin-left: 20px;
      margin-top: 5px;
    }
  }

  .quick-config {
    .el-button {
      margin-right: 10px;
      margin-bottom: 10px;
    }
  }
}

// 下拉选项样式优化
::v-deep .el-select-dropdown__item {
  height: auto;
  padding: 10px 20px;
  line-height: normal;
}
</style>
